import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { enquiryService } from '../api/axios';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF4560'];

function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await enquiryService.get('/enquiry/analytics');
        setData(response.data);
      } catch (err) {
        console.error("Error fetching analytics:", err);
        setError('Failed to load analytics data.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!data) return <Alert severity="info" sx={{ m: 2 }}>No analytics data available.</Alert>;

  return (
    <Box>
      <Typography variant="h3" component="h2" gutterBottom sx={{ mb: 3 }}>
        Business Analytics Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Row 1: Status Distribution & Monthly Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>Enquiry Status Distribution</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={data.statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>Monthly Enquiry Trend (Last 12 Months)</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={data.monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="value" stroke="#8884d8" activeDot={{ r: 8 }} name="Enquiries" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Row 2: SCM Workload & Lost Reasons */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>SCM Workload Distribution</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={data.scmWorkload} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#82ca9d" name="Active Enquiries" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom color="error">Top Reasons for Lost Enquiries</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={data.lostReasons}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#FF8042" name="Lost Count" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminAnalytics;